import { Component, computed, inject, OnInit, signal, viewChild } from '@angular/core';
import { sortBy } from 'lodash';
import { concat, Subject } from 'rxjs';
import { debounceTime, filter, toArray } from 'rxjs/operators';
import { NotifyService } from '../../service/notify.service';
import { UserAdminService } from '../../service/user-admin.service';
import { UserLoginService } from '../../service/user-login.service';
import { UserLocationResponse, LocationRole, SelectableLocationRole, UserProfile } from '../../types';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { UiModule } from '../../components/ui.module';
import { AdminUserManagementCreateUserComponent } from '../admin-user-management-create-user/admin-user-management-create-user.component';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { CdkVirtualScrollViewport, ScrollDispatcher, ScrollingModule } from '@angular/cdk/scrolling';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageSelectLocationComponent } from '../../secure/components/page-select-location/page-select-location.component';
import { possibleLocationRoles } from '../../constants';

type SelectableLocation = UserLocationResponse & {
  _selectableRoles: SelectableLocationRole[];
};

@Component({
  selector: 'app-admin-user-management-userlocations',
  templateUrl: './admin-user-management-user-locations.component.html',
  styleUrl: './admin-user-management-user-locations.component.scss',
  standalone: true,
  imports: [
    UiModule,
    SweetAlert2Module,
    FormsModule,
    ReactiveFormsModule,
    NgSelectModule,
    AdminUserManagementCreateUserComponent,
    ScrollingModule,
    PageSelectLocationComponent,
  ],
})
export class AdminUserManagementUserLocationsComponent implements OnInit {
  private adminService = inject(UserAdminService);
  private notifications = inject(NotifyService);
  public userLogin = inject(UserLoginService);

  private scrollDispatcher = inject(ScrollDispatcher);

  public selectedUser = signal<UserProfile | undefined>(undefined);

  public locationFilter = signal<string>('');
  public userFilter = signal<string>('');

  public hasLoadedUsers = signal<boolean>(false);
  public hasLoadedLocations = signal<boolean>(false);
  public hasLoadedPage = computed(() => this.hasLoadedUsers() && this.hasLoadedLocations());

  public allLocations = signal<SelectableLocation[]>([]);
  public users = signal<UserProfile[]>([]);

  public modalLocation = signal<SelectableLocation | undefined>(undefined);
  public modalRoles = signal<LocationRole[]>([]);

  public readonly possibleRoles: SelectableLocationRole[] = possibleLocationRoles;

  public visibleUserLocations = computed(() =>
    sortBy(
      this.allLocations()
        .filter((b) => this.selectedUser()?.roles.location[b.id])
        .filter((b) => b.name.toLowerCase().includes(this.locationFilter().toLowerCase())),
      (b) => b.name.toLowerCase(),
    ),
  );
  public visibleUsers = computed(() => this.users());

  public canSubmitModal = computed(() => this.modalLocation() && this.modalRoles().length > 0);

  public currentUserPage = signal<number>(1);
  public totalUsers = signal<number>(0);
  public hasMoreUsers = signal<boolean>(false);
  public isLoadingMoreUsers = signal<boolean>(false);

  public virtualScroll = viewChild<CdkVirtualScrollViewport>('userScroll');
  public searchInput = new Subject<string>();

  constructor() {
    this.searchInput
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(300))
      .subscribe((searchTerm: string) => {
        this.userFilter.set(searchTerm);
        this.loadMoreUsers(1);
      });

    this.scrollDispatcher
      .scrolled()
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(100))
      .pipe(filter(() => this.virtualScroll().getRenderedRange().end === this.virtualScroll().getDataLength()))
      .subscribe(() => {
        if (this.hasMoreUsers() && !this.isLoadingMoreUsers()) {
          this.loadMoreUsers(this.currentUserPage() + 1);
        }
      });
  }

  ngOnInit() {
    this.loadPage();
  }

  loadPage(): void {
    this.loadLocations();
    this.loadUsers();
  }

  public loadLocations() {
    this.hasLoadedLocations.set(false);

    this.adminService.getMyLocations().subscribe((locations) => {
      const pageLocations = locations as SelectableLocation[];

      pageLocations.forEach((location) => {
        location._selectableRoles = this.selectableRolesForLocation(location);
      });

      this.allLocations.set(pageLocations);
      this.hasLoadedLocations.set(true);
    });
  }

  public loadUsers() {
    this.hasLoadedUsers.set(false);
    this.users.set([]);

    this.loadMoreUsers(1);
  }

  public loadMoreUsers(page: number) {
    this.isLoadingMoreUsers.set(true);

    this.adminService
      .getMyUsersForBranches({
        page,
        items_per_page: 200,
        search: this.userFilter(),
      })
      .subscribe((usersData) => {
        this.hasMoreUsers.set(usersData.has_more);
        this.totalUsers.set(usersData.total_items);
        this.currentUserPage.set(usersData.page_number);

        if (usersData.page_number === 1) {
          this.users.set(usersData.items);
        } else {
          this.users.update((users) => [...users, ...usersData.items]);
        }

        this.isLoadingMoreUsers.set(false);
        this.hasLoadedUsers.set(true);
      });
  }

  selectUser(user: UserProfile): void {
    this.locationFilter.set('');
    this.selectedUser.set(user);
  }

  private selectableRolesForLocation(location: UserLocationResponse) {
    return this.possibleRoles.map((role) => ({
      ...role,
      disabled: !this.userLogin.canGrantLocationPermission(role.id, location.branch_id.toString(), location.id.toString()),
    }));
  }

  addUserRole(user: UserProfile, role: SelectableLocationRole, location: UserLocationResponse): void {
    this.adminService.grantLocationRole(user.user_id, location.id.toString(), role.id).subscribe(() => {
      this.notifications.notify(
        `Successfully granted permission "${role.name}" for user "${user.name || user.email}" in location "${location.name}"`,
      );
    });
  }

  removeUserRole(user: UserProfile, role: SelectableLocationRole, location: UserLocationResponse): void {
    this.adminService.revokeLocationRole(user.user_id, location.id.toString(), role.id).subscribe(() => {
      this.notifications.notify(
        `Successfully revoked permission "${role.name}" for user "${user.name || user.email}" in location "${location.name}"`,
      );

      if (user.roles.location[location.id].length === 0) {
        this.users.update((users) => users.filter((u) => u.user_id !== user.user_id));
      }
    });
  }

  addLocationToUser(): void {
    const chosenUser = structuredClone(this.selectedUser());

    chosenUser.roles.location[this.modalLocation().id] = this.modalRoles();

    concat(
      ...this.modalRoles().map((role) => this.adminService.grantLocationRole(chosenUser.user_id, this.modalLocation().id.toString(), role)),
    )
      .pipe(toArray())
      .subscribe(() => {
        this.notifications.notify(
          `Successfully added user "${chosenUser.name || chosenUser.email}" to location "${this.modalLocation().name}"`,
        );
        this.locationFilter.set('');
        this.modalRoles.set([]);
        this.modalLocation.set(undefined);
        this.selectedUser.set(chosenUser);
      });
  }

  removeUserPermissions(user: UserProfile, location: UserLocationResponse): void {
    concat(
      ...user.roles.location[location.id.toString()].map((role) =>
        this.adminService.revokeLocationRole(user.user_id, location.id.toString(), role),
      ),
    )
      .pipe(toArray())
      .subscribe(() => {
        this.notifications.notify(`Successfully removed user "${user.name || user.email}" from location "${location.name}"`);
        const userCopy = structuredClone(this.selectedUser());
        delete userCopy.roles.location[location.id.toString()];
        this.selectedUser.set(userCopy);
      });
  }

  addUserToList(user: UserProfile): void {
    this.users.update((users) => {
      return [...users, user];
    });

    this.userFilter.set(user.name);
    this.selectedUser.set(user);
  }
}
